import React, { KeyboardEvent, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import Text from 'components/core/Text';
import { Activity as ActivityProps } from 'reducers/activities/types';
import imageUrlGenerator from 'utils/imageUrlGenerator';
import { showModal } from 'actions/modal';
import { PONDER_EASE, PONDER_QUALITY, PONDER_DENSITY } from '../../constants';
import {
  Wrapper,
  StaticWrapper,
  Preview,
  PreviewWrapper,
  Metrics,
  ExerciseMetrics,
  Main,
  TagRow,
  TagChip,
  ProgramSession,
  ProgramNameWrapper,
  SessionDate,
} from './styles';

// Some activity thumbnails point at missing/placeholder assets. Swap the
// broken-image glyph for a transparent pixel so the Preview's own
// background-color shows through instead.
const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = TRANSPARENT_PIXEL;
};

const Activity = ({
  id,
  type,
  session,
  program,
  trackSessionId,
  ease,
  completedAt,
  quality,
  density,
  timeLength,
  exercisesCount,
  compact,
  workoutType,
  rating,
  tags,
  ponder,
  picture,
}: ActivityProps) => {
  const dispatch = useDispatch();
  const isExternal = type === 'external';

  const openExternalWorkout = useCallback(() => {
    dispatch(showModal('EXTERNAL_WORKOUT_DETAILS', { workoutId: id }));
  }, [dispatch, id]);

  const handleExternalKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openExternalWorkout();
      }
    },
    [openExternalWorkout],
  );
  const programLabel = useMemo(() => {
    if (type === 'programStart') {
      return 'Program started';
    }
    if (type === 'programEnd') {
      return 'Program completed';
    }
    if (type === 'external') {
      return 'External workout';
    }

    return program?.name || '';
  }, [type, program]);

  const easeLabel = useMemo(() => {
    switch (ease) {
      case 1:
      case 2:
      case 3:
      case 4:
        return PONDER_EASE[ease];
      default:
        return '';
    }
  }, [ease]);

  const qualityLabel = useMemo(() => {
    switch (quality) {
      case 1:
      case 2:
      case 3:
      case 4:
        return PONDER_QUALITY[quality];
      default:
        return '';
    }
  }, [quality]);

  const densityLabel = density ? PONDER_DENSITY[density] || '' : '';

  // External workouts carry a single "how did it feel?" rating on the ease scale
  const ratingLabel = useMemo(() => {
    switch (rating) {
      case 1:
      case 2:
      case 3:
      case 4:
        return PONDER_EASE[rating];
      default:
        return '';
    }
  }, [rating]);

  const sessionName = useMemo(() => {
    if (type === 'programStart') return `👊 You started program ${program?.name}`;
    if (type === 'programEnd') return `🏆 You completed program ${program?.name}`;
    if (type === 'external') return workoutType || '';

    return session?.name || '';
  }, [program?.name, session?.name, workoutType, type]);

  const sessionDescription = useMemo(() => {
    if (type === 'programStart' || type === 'programEnd') return program?.description;
    if (type === 'external') return ponder;

    return session?.description;
  }, [program?.description, session?.description, ponder, type]);

  if (!session && !isExternal) return null;

  const previewPicture = isExternal ? picture : session?.picture;

  const content = (
    <>
      <Main>
        <ProgramNameWrapper>
          <Text variant="captionSmall">{programLabel}</Text>
          {completedAt && <SessionDate variant="captionSmall">{completedAt}</SessionDate>}
        </ProgramNameWrapper>
        {!compact && (
          <Text
            variant="textSmall"
            tag="h3"
            weight="bold"
            color="black"
            transform="uppercase"
            mb={3}
          >
            {sessionName}
          </Text>
        )}
        <PreviewWrapper>
          <Preview
            src={imageUrlGenerator(previewPicture, '200x200', '100x100')}
            alt=""
            onError={handleImageError}
          />
          {type === 'workout' ? (
            <Metrics>
              {!!easeLabel && <Text variant="captionSmall">Ease: {easeLabel}</Text>}
              {!!qualityLabel && <Text variant="captionSmall">Quality: {qualityLabel}</Text>}
              {!!densityLabel && <Text variant="captionSmall">Density: {densityLabel}</Text>}
            </Metrics>
          ) : (
            <div>
              {compact && (
                <Text
                  variant="textSmall"
                  tag="h3"
                  weight="bold"
                  color="black"
                  transform="uppercase"
                  mb={1}
                >
                  {sessionName}
                </Text>
              )}
              {isExternal && !!rating && (
                <Metrics style={{ marginBottom: 8 }}>
                  <Text variant="captionSmall">Felt: {ratingLabel}</Text>
                </Metrics>
              )}
              <ProgramSession variant="textSmall" compact={compact}>
                {sessionDescription}
              </ProgramSession>
            </div>
          )}
        </PreviewWrapper>
      </Main>
      {(type === 'workout' || (isExternal && !!timeLength)) && (
        <ExerciseMetrics>
          <div>
            <Text variant="captionSmall">Minutes</Text>
            <Text variant="mainHeader" tag="p" mb={[0, 4]}>
              {timeLength}
            </Text>
          </div>
          {type === 'workout' && (
            <div>
              <Text variant="captionSmall">moves</Text>
              <Text variant="mainHeader" tag="p">
                {exercisesCount}
              </Text>
            </div>
          )}
        </ExerciseMetrics>
      )}
      {!!tags?.length && (
        <TagRow aria-label="Tags">
          {tags.map(({ id, label }) => (
            <TagChip key={id}>{label}</TagChip>
          ))}
        </TagRow>
      )}
    </>
  );

  if (isExternal) {
    // A div rather than a <button>: the card holds headings and a list
    return (
      <StaticWrapper
        compact={compact}
        role="button"
        tabIndex={0}
        aria-label={`View ${workoutType || 'workout'} details`}
        onClick={openExternalWorkout}
        onKeyDown={handleExternalKeyDown}
      >
        {content}
      </StaticWrapper>
    );
  }

  return (
    <Wrapper
      href={`/programs/${program?.id}/sessions/${session?.id}${
        trackSessionId ? `?trackSessionId=${trackSessionId}` : ''
      }`}
      compact={compact}
    >
      {content}
    </Wrapper>
  );
};

export default React.memo(Activity);
